import React from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { Control, Controller, FieldValues, Path } from "react-hook-form";
import { cn } from "@onclicks/sharedcomponents/ui";
import Icon from "../shared/ui/icon";

export interface SelectOption {
  value: string | number;
  label: string;
  className?: string;
  /** Option montrée mais non sélectionnable — l'expliquer via `hint`. */
  disabled?: boolean;
  /** Précision affichée sous le libellé, typiquement le motif d'une option désactivée. */
  hint?: string;
}

/** Ce que le menu déroulant sait afficher, indépendamment d'où vient la valeur. */
interface SelectInputProps {
  value: string | number | null | undefined;
  /** Reçoit `null` quand l'option `emptyLabel` est choisie. */
  onChange: (value: string | number | null) => void;
  titre: string;
  options: SelectOption[];
  /** Ajoute en tête une option qui vide la valeur (`null`), pour les champs facultatifs. */
  emptyLabel?: string;
  icon?: string;
  placeholder: string;
  containerClassName?: string;
  error?: string;
  disabled?: boolean;
  description?: string;
}

/**
 * Menu déroulant contrôlé, sans react-hook-form. Extrait de `Select` pour les formulaires qui
 * tiennent leur état à la main — les valeurs des champs personnalisés d'entreprise, notamment,
 * vivent dans un `useState` et n'ont pas de `control` à fournir.
 */
export function SelectInput({
  value,
  onChange,
  titre,
  options: providedOptions,
  emptyLabel,
  icon,
  placeholder,
  containerClassName,
  error,
  disabled,
  description,
}: SelectInputProps) {
  // La valeur vide se représente par "" dans le Listbox, qui ne connaît pas `null`.
  const options: SelectOption[] = emptyLabel
    ? [{ value: "", label: emptyLabel }, ...providedOptions]
    : providedOptions;
  const selectedOption = options.find((o) => o.value === (value ?? "")) || null;

  return (
    <div className={cn("space-y-2", containerClassName)}>
      <label className="text-sm font-semibold text-slate-700 dark:text-white">
        {titre}
      </label>

      <Listbox
        value={value ?? ""}
        onChange={(choice) => onChange(emptyLabel && choice === "" ? null : choice)}
        disabled={disabled}
      >
        {({ open }) => (
          <div className="relative">
            <ListboxButton
              className={cn(
                "w-full bg-slate-50 text-slate-800 rounded-lg p-3 text-left border border-slate-200 dark:border-slate-300",
                "transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary",
                "cursor-pointer flex items-center justify-between",
                error && "ring-2 ring-red-500",
                icon && "pl-10",
                disabled && "cursor-not-allowed opacity-60",
              )}
            >
              {icon && (
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Icon name={icon} className="text-lg text-slate-400" />
                </span>
              )}

              <span
                className={cn("truncate", !selectedOption && "text-slate-400")}
              >
                {selectedOption ? selectedOption.label : placeholder}
              </span>

              <Icon
                name="expand_more"
                className={cn(
                  "text-lg text-slate-400 transition-transform duration-200",
                  open && "rotate-180",
                )}
              />
            </ListboxButton>

            <ListboxOptions
              anchor="bottom start"
              className={cn(
                "w-(--button-width) mt-2 p-1.5 rounded-xl bg-white dark:bg-slate-900",
                "shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800",
                "focus:outline-none overflow-auto max-h-60 z-50",
                "transition duration-200 data-closed:opacity-0 data-closed:scale-95",
              )}
            >
              {options.map((option) => (
                <ListboxOption
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                  className={cn(
                    "group flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 mb-0.5 last:mb-0",
                    "cursor-pointer transition-all duration-150 dark:text-white",
                    "data-focus:bg-primary/10 data-focus:text-primary",
                    "data-disabled:cursor-not-allowed data-disabled:opacity-50 data-disabled:data-focus:bg-transparent data-disabled:data-focus:text-slate-800",
                    "text-slate-800",
                    option.className,
                  )}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{option.label}</span>
                    {option.hint && (
                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                        {option.hint}
                      </span>
                    )}
                  </span>
                  {(value ?? "") === option.value && (
                    <Icon name="check" className="text-primary text-xl" />
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        )}
      </Listbox>

      {description && !error && (
        <p className="text-sm! text-slate-500 dark:text-white italic">{description}</p>
      )}

      {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
    </div>
  );
}

interface FormSelectProps<T extends FieldValues>
  extends Omit<SelectInputProps, "value" | "onChange"> {
  control: Control<T>;
  name: Path<T>;
  rules?: object;
  defaultValue?: string | number;
}

/** Le même menu déroulant, branché sur un champ react-hook-form. */
export function Select<T extends FieldValues>({
  control,
  name,
  rules,
  defaultValue,
  ...presentation
}: FormSelectProps<T>) {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue={defaultValue as never}
      render={({ field: { value, onChange } }) => (
        <SelectInput {...presentation} value={value} onChange={onChange} />
      )}
    />
  );
}
